/**
 * Assistant concierge schedule-setup mutation hooks (AC-1790, epic AC-1787).
 *
 * Backs the FINAL concierge step — when the user's email-summary goal implies
 * recurrence, the journey surfaces a `ScheduleConfirmCard` inside `AssistantChat`
 * so the user can create a recurring schedule for the just-built workflow without
 * leaving chat:
 *
 *   - `useConfirmScheduleSetup()` -> POST .../actions/{aid}/confirm-schedule
 *   - `useDeclineScheduleSetup()` -> POST .../actions/{aid}/decline-schedule
 *
 * The schedule cadence (preset+time / interval, timezone, enabled) is ephemeral
 * card form state; the OrchestrationSchedule row it writes is server state owned
 * by React Query. Both mutations are pessimistic — the server's response is the
 * truth.
 *
 * ## Invalidation contract
 *
 * On SUCCESS each invalidates `queryKeys.schedule(orchestrationId)` so any
 * ScheduleConfigPanel / ScheduleIndicator subscriber on that workflow re-reads
 * and the new (or, for decline, unchanged) schedule surfaces without a manual
 * refetch. On FAILURE `mutation.error`/`isError` surface and NOTHING is
 * invalidated — a failed mutation must not perturb the cache
 * (decline-without-residue / no-spurious-refetch invariant). React Query only
 * runs `onSuccess` when the `mutationFn` resolves, so the invalidation is
 * automatically skipped on reject.
 *
 * Structure mirrors `useConfirmBuiltinSetup` / `useDeclineBuiltinSetup`
 * (single pessimistic write with an explicit `onSuccess` invalidation set).
 */

import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import apiService from '../../services/api';
import { AssistantChatResponse, ScheduleConfirmRequest } from '../../types';
import { queryKeys } from './queryKeys';

/** Args for `useConfirmScheduleSetup().mutate(...)`. */
export interface ConfirmScheduleSetupVars {
  conversationId: number;
  actionId: number;
  request: ScheduleConfirmRequest;
}

/** Args for `useDeclineScheduleSetup().mutate(...)`. */
export interface DeclineScheduleSetupVars {
  conversationId: number;
  actionId: number;
  /**
   * The workflow whose schedule key to invalidate on decline. Carried so a
   * decline (which writes nothing) still re-settles any open subscriber on that
   * workflow's schedule read.
   */
  orchestrationId: number;
}

/**
 * `useConfirmScheduleSetup()` (AC-1790, epic AC-1787) — confirms a pending
 * `configure_schedule` action, creating a recurring schedule for the just-built
 * email-summary workflow (via `OrchestrationScheduleService.create_schedule`
 * server-side). On SUCCESS invalidates `queryKeys.schedule(orchestrationId)` so
 * any ScheduleConfigPanel / ScheduleIndicator subscriber re-reads. On FAILURE
 * surfaces `mutation.error` and invalidates nothing.
 */
export function useConfirmScheduleSetup(): UseMutationResult<
  AssistantChatResponse,
  Error,
  ConfirmScheduleSetupVars
> {
  const queryClient = useQueryClient();
  return useMutation<AssistantChatResponse, Error, ConfirmScheduleSetupVars>({
    mutationFn: ({ conversationId, actionId, request }) =>
      apiService.confirmScheduleSetup(conversationId, actionId, request),
    onSuccess: (data, variables) => {
      // AC-1788 (Epic AC-1787 fix, MINOR): invalidate using the AUTHORITATIVE
      // orchestration id the server scheduled (from the executed action's
      // result), not the advisory request var. The backend takes the
      // orchestration id from the STORED pending action's action_data, so the
      // request's orchestration_id is advisory only -- preferring the response's
      // id keeps the invalidated key aligned with the workflow actually scheduled.
      const authoritativeId =
        data?.actions?.find((a) => a.result?.orchestration_id != null)?.result
          ?.orchestration_id ?? variables.request.orchestration_id;
      queryClient.invalidateQueries({
        queryKey: queryKeys.schedule(authoritativeId),
      });
    },
  });
}

/**
 * `useDeclineScheduleSetup()` (AC-1790, epic AC-1787) — declines a pending
 * `configure_schedule` action so no schedule is created (decline-without-residue).
 * On SUCCESS invalidates `queryKeys.schedule(orchestrationId)` so the (unchanged)
 * read re-settles; on FAILURE surfaces `mutation.error` and invalidates nothing.
 */
export function useDeclineScheduleSetup(): UseMutationResult<
  AssistantChatResponse,
  Error,
  DeclineScheduleSetupVars
> {
  const queryClient = useQueryClient();
  return useMutation<AssistantChatResponse, Error, DeclineScheduleSetupVars>({
    mutationFn: ({ conversationId, actionId }) =>
      apiService.declineScheduleSetup(conversationId, actionId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.schedule(variables.orchestrationId),
      });
    },
  });
}

export default useConfirmScheduleSetup;
